/**
 * Stories Service
 * 
 * This service handles fetching and managing stories data.
 * In a real application, this would likely fetch from an API.
 * For now, we're using mock data.
 */

// Mock data for stories
const mockStories = [
  {
    id: 1,
    title: 'Welcome to Team Stories!',
    content: 'Stay updated with the latest team news and announcements.',
    imageUrl: 'https://via.placeholder.com/600x800/3498db/ffffff?text=Team+News',
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
  },
  {
    id: 2,
    title: 'Upcoming Match',
    content: 'This Saturday at 3:00 PM. Don\'t forget to confirm your attendance!',
    imageUrl: 'https://via.placeholder.com/600x800/e74c3c/ffffff?text=Match+Day',
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
  },
  {
    id: 3,
    title: 'Team Achievement',
    content: 'Congratulations on the recent win! Final score: 3-1. Great teamwork from everyone!',
    imageUrl: 'https://via.placeholder.com/600x800/27ae60/ffffff?text=Victory',
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
  },
];

// Local storage keys
const STORIES_KEY = 'team_stories';
const VIEWED_STORIES_KEY = 'viewed_stories';

/**
 * Get all active stories
 * @returns {Promise<Array>} Array of story objects
 */
const getStories = async () => {
  // In a real app, this would be an API call
  // For now, we'll use the mock data and local storage
  
  try {
    // Check if we have stories in local storage
    const storedStories = localStorage.getItem(STORIES_KEY);
    
    if (storedStories) {
      const parsedStories = JSON.parse(storedStories);
      // Filter out expired stories
      const now = new Date().toISOString();
      return parsedStories.filter(story => story.expiresAt > now);
    } else {
      // If no stories in storage, use mock data and save to storage
      localStorage.setItem(STORIES_KEY, JSON.stringify(mockStories));
      return mockStories;
    }
  } catch (error) {
    console.error('Error getting stories:', error);
    return [];
  }
};

/**
 * Mark a story as viewed by the current user
 * @param {number} storyId - The ID of the viewed story
 * @returns {Promise<void>}
 */
const markStoryAsViewed = async (storyId) => {
  try {
    const playerId = sessionStorage.getItem('player_id');
    if (!playerId) return;
    
    // Get currently viewed stories
    const viewedStoriesStr = localStorage.getItem(VIEWED_STORIES_KEY) || '{}';
    const viewedStories = JSON.parse(viewedStoriesStr);
    
    // Initialize player's viewed stories if not exists
    if (!viewedStories[playerId]) {
      viewedStories[playerId] = [];
    }
    
    // Add story to viewed if not already there
    if (!viewedStories[playerId].includes(storyId)) {
      viewedStories[playerId].push(storyId);
      localStorage.setItem(VIEWED_STORIES_KEY, JSON.stringify(viewedStories));
    }
  } catch (error) {
    console.error('Error marking story as viewed:', error);
  }
};

/**
 * Check if there are any unviewed stories for the current user
 * @returns {Promise<boolean>} True if there are unviewed stories
 */
const hasUnviewedStories = async () => {
  try {
    const playerId = sessionStorage.getItem('player_id');
    if (!playerId) return false;
    
    // Get all active stories
    const stories = await getStories();
    
    // Get viewed stories for this player
    const viewedStoriesStr = localStorage.getItem(VIEWED_STORIES_KEY) || '{}';
    const viewedStories = JSON.parse(viewedStoriesStr);
    const playerViewedStories = viewedStories[playerId] || [];
    
    // Check if there are any stories not in the viewed list
    return stories.some(story => !playerViewedStories.includes(story.id));
  } catch (error) {
    console.error('Error checking for unviewed stories:', error);
    return false;
  }
};

/**
 * Mark all stories as viewed by the current user
 * @returns {Promise<void>}
 */
const markAllStoriesAsViewed = async () => {
  try {
    const playerId = sessionStorage.getItem('player_id');
    if (!playerId) return;
    
    // Get all active stories
    const stories = await getStories();
    
    // Get viewed stories for this player
    const viewedStoriesStr = localStorage.getItem(VIEWED_STORIES_KEY) || '{}';
    const viewedStories = JSON.parse(viewedStoriesStr);
    
    // Mark all stories as viewed
    viewedStories[playerId] = stories.map(story => story.id);
    localStorage.setItem(VIEWED_STORIES_KEY, JSON.stringify(viewedStories));
  } catch (error) {
    console.error('Error marking all stories as viewed:', error);
  }
};

// Export the service functions
const storiesService = {
  getStories,
  markStoryAsViewed,
  hasUnviewedStories,
  markAllStoriesAsViewed
};

export default storiesService; 